import { useMinimumButtonLabel } from "~/modules/catering/components/MinimumOrder/MinimumOrder";
import { useContext, useRef, useState } from "react";
import clsx from "clsx";
import { useNavigate, useParams, useSearchParams } from "@remix-run/react";

import TaxContext from "~/contexts/TaxContext";
import OrderContext from "~/contexts/OrderContext";
import ThemeContext from "~/contexts/ThemeContext";
import Button from "~/components/Button/Button";
import { useOutsideClick } from "~/hooks/useOutsideClick";
import { getCartItemCount } from "~/helpers/cartHelpers";

import styles from "./CartDropdown.module.css";
import { urlGen } from "~/helpers/routesHelpers";
import SiteContext from "~/contexts/SiteContext";
import Cart from "../Cart/Cart";
import { NavBarSize } from "~/theme/models/themeModels";
import { getLuma } from "~/helpers/colorHelpers";

const CartDropdown = () => {
  const [visibleDropdown, setVisibleDropdown] = useState(false);

  const { orderSummary, orderSource } = useContext(OrderContext);
  const { formatAmount } = useContext(TaxContext);
  // Held back below the minimum order: "Add $62.00 more to check out"
  const belowMinimum = useMinimumButtonLabel("check out");
  const { template } = useContext(ThemeContext);

  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const { site } = useContext(SiteContext);
  const [searchParams] = useSearchParams();
  const menuCode = searchParams.get("menuCode");
  const params = useParams();

  useOutsideClick([triggerRef, dropdownRef], () => setVisibleDropdown(false));

  const handleCheckoutClick = () => {
    setVisibleDropdown(false);
    navigate(
      urlGen("Checkout", {
        orderSource,
        siteId: site?.id,
        menuCode,
        tableNumber: params.table,
      }),
    );
  };

  // Styles
  const backgroundColor = template.cart.backgroundColour;

  const showAdditionalProducts = template.cart.showAdditionalProducts;

  const cartTriggerFontColor =
    getLuma(template.navBar.backgroundColour) < 150 ? "#fff" : "#000";
  const cartTriggerIndicatorFontColor = template.navBar.backgroundColour;
  const cartTriggerHeight = NavBarSize[template.navBar.size];

  return (
    <div className={styles.wrapper}>
      <div
        ref={triggerRef}
        className={styles.trigger}
        onClick={() => setVisibleDropdown(!visibleDropdown)}
        style={{ lineHeight: cartTriggerHeight }}
      >
        <span style={{ color: cartTriggerFontColor }}>
          {formatAmount(orderSummary?.finalPriceIncTax ?? 0)}
        </span>
        <div
          className={styles.countIndicator}
          style={{
            color: cartTriggerIndicatorFontColor,
            backgroundColor: cartTriggerFontColor,
          }}
        >
          {getCartItemCount(orderSummary.lines)}
        </div>
      </div>
      <div
        ref={dropdownRef}
        className={clsx(
          styles.dropdown,
          !visibleDropdown && styles.dropdownHidden,
        )}
        style={{
          backgroundColor,
        }}
      >
        <div className={styles.cartContent}>
          {!!orderSummary.lines.length && (
            <div className={styles.buttonWrapper}>
              <Button onClick={handleCheckoutClick} disabled={!!belowMinimum}>
                <span>
                  {belowMinimum ?? (
                    <>
                      Go to checkout{" "}
                      <b>{formatAmount(orderSummary?.finalPriceIncTax ?? 0)}</b>
                    </>
                  )}
                </span>
              </Button>
            </div>
          )}
          <Cart
            showAdditionalProducts={showAdditionalProducts}
            readOnly={false}
            clearable
            hideAmountsForMobile={false}
          />
        </div>
      </div>
    </div>
  );
};

export default CartDropdown;
